import { getAnonymousId, markResumeCompleted, readResumeState, saveResumeState } from './activityIntelligence';
import { enqueueResumeSync } from './resumeSync';

const MIN_RESUME_SECONDS = 10;
const COMPLETION_THRESHOLD = 0.95;

const normalizeProgressSample = ({ positionSeconds, durationSeconds } = {}) => {
  const position = Math.max(0, Number(positionSeconds) || 0);
  const duration = Math.max(0, Number(durationSeconds) || 0);
  const progress = duration > 0 ? Math.min(1, position / duration) : 0;
  return { position, duration, progress };
};

const enqueuePlayerResume = ({ entity, sample, metadata, completed }) => enqueueResumeSync({
  anonymous_id: getAnonymousId(),
  entity_id: entity.id,
  entity_type: entity.type || 'content',
  position_seconds: sample.position,
  duration_seconds: sample.duration,
  completed,
  metadata: { ...metadata, entity, updated_at: new Date().toISOString() },
});

const persistProgressSample = ({ entity, positionSeconds, durationSeconds, metadata = {} } = {}) => {
  if (!entity?.id) return { saved: false, completed: false };
  const sample = normalizeProgressSample({ positionSeconds, durationSeconds });
  if (sample.duration <= 0 || sample.position < MIN_RESUME_SECONDS) {
    return { saved: false, completed: false, ...sample };
  }
  if (sample.progress >= COMPLETION_THRESHOLD) {
    markResumeCompleted(entity, { ...metadata, position_seconds: sample.position, duration_seconds: sample.duration });
    enqueuePlayerResume({ entity, sample, metadata, completed: true });
    return { saved: true, completed: true, ...sample };
  }
  saveResumeState(entity, sample.position, sample.duration, metadata);
  enqueuePlayerResume({ entity, sample, metadata, completed: false });
  return { saved: true, completed: false, ...sample };
};

const readYouTubeProgress = (player) => {
  if (!player || typeof player.getCurrentTime !== 'function' || typeof player.getDuration !== 'function') return null;
  try {
    return normalizeProgressSample({
      positionSeconds: player.getCurrentTime(),
      durationSeconds: player.getDuration(),
    });
  } catch {
    return null;
  }
};

const readAudioProgress = (audio) => {
  if (!audio) return null;
  try {
    return normalizeProgressSample({
      positionSeconds: audio.currentTime,
      durationSeconds: audio.duration,
    });
  } catch {
    return null;
  }
};

const audioEntityFromTrack = (track = {}) => {
  if (!track || track.type === 'radio' || track.type === 'youtube') return null;
  const id = track.id || track.track_id || track.externalUrl || track.url || track.title;
  if (!id) return null;
  return {
    id: String(id),
    type: track.type || 'track',
    title: track.title,
    summary: track.artist,
    media: track.cover ? [{ kind: 'image', url: track.cover }] : [],
  };
};

const findResumeItem = (entity) => {
  if (!entity?.id) return null;
  return readResumeState().find((item) => item.entity?.id === entity.id && item.entity?.type === entity.type) || null;
};

const restoreAudioPosition = (audio, entity) => {
  const item = findResumeItem(entity);
  if (!audio || !item || !Number.isFinite(item.position_seconds) || item.position_seconds < MIN_RESUME_SECONDS) return 0;
  const duration = Number(audio.duration || item.duration_seconds || 0);
  const safePosition = duration > 0 ? Math.min(item.position_seconds, Math.max(0, duration - 1)) : item.position_seconds;
  try {
    audio.currentTime = safePosition;
    return safePosition;
  } catch {
    return 0;
  }
};

export {
  COMPLETION_THRESHOLD,
  MIN_RESUME_SECONDS,
  audioEntityFromTrack,
  findResumeItem,
  normalizeProgressSample,
  persistProgressSample,
  readAudioProgress,
  readYouTubeProgress,
  restoreAudioPosition,
};
